import '@awesome.me/webawesome/dist/components/button/button.js';

import type { SurfacedItem } from '@app/core';
import { useCallback, useState } from 'react';

import type { AppDatabase } from '../services/database.js';
import { seedDatabase } from '../services/seed.js';
import type { SimulatedTimeProvider } from '../services/time-provider.js';
import { useUIStore } from '../stores/ui-store.js';

interface DebugPanelProps {
  queue: SurfacedItem[];
  timeProvider: SimulatedTimeProvider;
  onTimeChange: () => void;
  database: AppDatabase;
}

// Dev-only guard — this component is not rendered in production builds
export function DebugPanel({ queue, timeProvider, onTimeChange, database }: DebugPanelProps) {
  if (!import.meta.env.DEV) return null;

  return (
    <DebugPanelInner
      queue={queue}
      timeProvider={timeProvider}
      onTimeChange={onTimeChange}
      database={database}
    />
  );
}

function DebugPanelInner({ queue, timeProvider, onTimeChange, database }: DebugPanelProps) {
  const debugPanelOpen = useUIStore(s => s.debugPanelOpen);
  const toggleDebugPanel = useUIStore(s => s.toggleDebugPanel);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const advanceTime = useCallback(
    (days: number) => {
      timeProvider.advanceDays(days);
      onTimeChange();
    },
    [timeProvider, onTimeChange],
  );

  const resetTime = useCallback(() => {
    timeProvider.reset();
    onTimeChange();
  }, [timeProvider, onTimeChange]);

  const handleSeed = useCallback(() => {
    void seedDatabase(database).then(() => {
      onTimeChange();
    });
  }, [database, onTimeChange]);

  if (!debugPanelOpen) {
    return (
      <button
        onClick={toggleDebugPanel}
        style={{
          position: 'fixed',
          bottom: 'var(--space-sm)',
          right: 'var(--space-sm)',
          background: 'var(--color-card)',
          border: '1px solid var(--color-card-border)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-xs) var(--space-sm)',
          color: 'var(--color-text-muted)',
          fontSize: '0.7rem',
          fontFamily: 'var(--font-mono)',
          cursor: 'pointer',
          zIndex: 200,
        }}
      >
        debug
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        maxHeight: '50vh',
        overflow: 'auto',
        background: 'var(--color-bg)',
        borderTop: '1px solid var(--color-card-border)',
        padding: 'var(--space-md)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.75rem',
        zIndex: 200,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-sm)',
        }}
      >
        <span style={{ fontWeight: 600 }}>Debug Panel</span>
        <button
          onClick={toggleDebugPanel}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-md)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ color: 'var(--color-text-secondary)' }}>
          Offset: {String(Math.round(timeProvider.getOffset() / 86_400_000))}d
        </span>
        <wa-button
          size="small"
          variant="neutral"
          onClick={() => {
            advanceTime(1);
          }}
        >
          +1d
        </wa-button>
        <wa-button
          size="small"
          variant="neutral"
          onClick={() => {
            advanceTime(3);
          }}
        >
          +3d
        </wa-button>
        <wa-button
          size="small"
          variant="neutral"
          onClick={() => {
            advanceTime(7);
          }}
        >
          +7d
        </wa-button>
        <wa-button
          size="small"
          variant="neutral"
          onClick={() => {
            advanceTime(14);
          }}
        >
          +14d
        </wa-button>
        <wa-button
          size="small"
          variant="neutral"
          onClick={() => {
            advanceTime(30);
          }}
        >
          +30d
        </wa-button>
        <wa-button size="small" variant="neutral" appearance="outlined" onClick={resetTime}>
          Reset
        </wa-button>
        <wa-button size="small" variant="neutral" appearance="outlined" onClick={handleSeed}>
          Seed DB
        </wa-button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-card-border)', textAlign: 'left' }}>
            <th style={{ padding: 'var(--space-xs)' }}>Item</th>
            <th style={{ padding: 'var(--space-xs)' }}>Temp</th>
            <th style={{ padding: 'var(--space-xs)' }}>Effort</th>
            <th style={{ padding: 'var(--space-xs)' }}>Fresh</th>
            <th style={{ padding: 'var(--space-xs)' }}>Total</th>
            <th style={{ padding: 'var(--space-xs)' }}>Fading</th>
          </tr>
        </thead>
        <tbody>
          {queue.map(s => {
            const { curve } = s.item;
            const isExpanded = expandedItem === s.item.id;
            return (
              <tr key={s.item.id} style={{ borderBottom: '1px solid var(--color-card-border)' }}>
                <td
                  style={{
                    padding: 'var(--space-xs)',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setExpandedItem(isExpanded ? null : s.item.id);
                  }}
                >
                  {s.item.text}
                  {isExpanded && (
                    <div
                      style={{
                        whiteSpace: 'normal',
                        fontSize: '0.65rem',
                        color: 'var(--color-text-secondary)',
                        marginTop: '4px',
                      }}
                    >
                      <div>dir: {curve.direction}</div>
                      <div>start: {String(curve.startingTemperature)}</div>
                      {curve.direction === 'warming' && <div>ceil: {String(curve.ceiling)}</div>}
                      <div>easing: {curve.easing}</div>
                      {curve.direction === 'warming' && curve.anchorDate && (
                        <div>anchor: {curve.anchorDate.toISOString().slice(0, 10)}</div>
                      )}
                      {curve.duration != null && <div>dur: {String(curve.duration)}d</div>}
                    </div>
                  )}
                </td>
                <td style={{ padding: 'var(--space-xs)' }}>{s.temperature.toFixed(1)}</td>
                <td style={{ padding: 'var(--space-xs)' }}>
                  {s.factors?.effortBonus.toFixed(1) ?? '-'}
                </td>
                <td style={{ padding: 'var(--space-xs)' }}>
                  {s.factors?.freshnessBonus.toFixed(1) ?? '-'}
                </td>
                <td style={{ padding: 'var(--space-xs)' }}>{s.factors?.total.toFixed(1) ?? '-'}</td>
                <td style={{ padding: 'var(--space-xs)' }}>{s.fading ? '⚠' : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
